/**
 * Derive a safe filename for a downloaded file.
 *
 * Media files (census, analysis, presentations) are served from S3/Tigris via
 * short-lived **presigned** URLs. Those URLs carry the object key in the path and
 * a long query string, e.g.
 *
 *   https://host/mediafiles/quotes/census/Foo_3vWlvth.xlsx
 *     ?response-content-disposition=attachment%3B%20filename%3D%22Foo_3vWlvth.xlsx%22
 *     &response-content-type=...&X-Amz-Algorithm=...&X-Amz-Signature=...
 *
 * Taking the last `/`-separated segment of the *raw* URL therefore captures the
 * entire query string too. When that string is used as an `<a download>` name the
 * browser sanitises the `?`/`&`/`"` characters into the filename, producing
 * unusable names like `Foo_3vWlvth.xlsx_response-content-disposition=...&X-Amz-Signature=...`
 * that end in the signature and have no `.xlsx`/`.pptx` extension.
 *
 * The fix: prefer an explicit server-provided filename when we have one, otherwise
 * strip the query string (and fragment) before taking the basename.
 *
 * @param url          The (possibly presigned) file URL.
 * @param serverName   Filename supplied by the API (e.g. `census_file_name`), if any.
 * @param fallback     Name to use when nothing else is available.
 * @param defaultExtension Extension (e.g. `.xlsx`) applied **only** when the resolved
 *   name has no extension of its own. Census/analysis files are always stored with a
 *   real extension, but this guarantees a usable one if the name ever arrives bare —
 *   without clobbering a legitimate `.xls`/`.csv` census file.
 */
export function getDownloadFileName(
  url: string | null | undefined,
  serverName?: string | null,
  fallback = 'download',
  defaultExtension?: string,
): string {
  const name = resolveFileName(url, serverName, fallback);
  return defaultExtension ? withDefaultExtension(name, defaultExtension) : name;
}

function resolveFileName(
  url: string | null | undefined,
  serverName: string | null | undefined,
  fallback: string,
): string {
  const trimmedServerName = serverName?.trim();
  if (trimmedServerName) {
    return trimmedServerName;
  }

  if (!url) {
    return fallback;
  }

  // Drop the query string and fragment so signed-URL params never leak into the
  // filename, then take the last path segment.
  const pathPart = url.split('?')[0].split('#')[0];
  const segments = pathPart.split('/');
  const lastSegment = segments[segments.length - 1];

  if (!lastSegment) {
    return fallback;
  }

  try {
    return decodeURIComponent(lastSegment);
  } catch {
    // Malformed percent-encoding: fall back to the raw segment.
    return lastSegment;
  }
}

/** Whether `name` already ends in a plausible file extension (e.g. `.xlsx`, `.csv`). */
function hasExtension(name: string): boolean {
  return /\.[A-Za-z0-9]{1,8}$/.test(name);
}

/** Append `ext` only when `name` has no extension of its own. */
function withDefaultExtension(name: string, ext: string): string {
  if (hasExtension(name)) {
    return name;
  }
  const normalisedExt = ext.startsWith('.') ? ext : `.${ext}`;
  return `${name}${normalisedExt}`;
}
